import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { createEnum } from "@/lib/utils/createEnum";
import { useTranslation } from "react-i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type C3DedicatedFund = {
  id: number;
  date?: string;
  strategy_from_id?: number;
  strategy_from_name?: string;
  strategy_to_id?: number;
  strategy_to_name?: string;
  quantity?: number;
  weight?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
  exposition_local_ccy?: number;
  exposition_ptf_ccy?: number;
};

export const c3DedicatedFundFields = createEnum<C3DedicatedFund>();

export const useC3DedicatedFundColumns = () => {
  const { t } = useTranslation([Tables.C3_DEDICATED_FUNDS, "tables"]);

  const columns: ColumnDef<C3DedicatedFund>[] = [
    {
      accessorKey: c3DedicatedFundFields.id,
      header: "Id",
    },
    {
      accessorKey: c3DedicatedFundFields.date,
      header: () => t("columns.date", { ns: Tables.C3_DEDICATED_FUNDS }),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(c3DedicatedFundFields.date)))}
        </div>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.strategy_from_name,
      header: () =>
        t("columns.strategy_from_name", { ns: Tables.C3_DEDICATED_FUNDS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_from_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_from_name}
        </Link>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.strategy_to_name,
      header: () =>
        t("columns.strategy_to_name", { ns: Tables.C3_DEDICATED_FUNDS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_to_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_to_name}
        </Link>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.quantity,
      header: () => (
        <div className="text-right">
          {t("columns.quantity", { ns: Tables.C3_DEDICATED_FUNDS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3DedicatedFundFields.quantity))}
        </div>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.weight,
      header: () => (
        <div className="text-right">
          {t("columns.weight", { ns: Tables.C3_DEDICATED_FUNDS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3DedicatedFundFields.weight))}
        </div>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.market_value_ptf_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_ptf_ccy", { ns: Tables.C3_DEDICATED_FUNDS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(
            row.getValue(c3DedicatedFundFields.market_value_ptf_ccy),
          )}
        </div>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.exposition_local_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.exposition_local_ccy", { ns: Tables.C3_DEDICATED_FUNDS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(
            row.getValue(c3DedicatedFundFields.exposition_local_ccy),
          )}
        </div>
      ),
    },
    {
      accessorKey: c3DedicatedFundFields.exposition_ptf_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.exposition_ptf_ccy", { ns: Tables.C3_DEDICATED_FUNDS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3DedicatedFundFields.exposition_ptf_ccy))}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(c3DedicatedFundFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      ),
    },
  ];

  return columns;
};
